'use client';

import type {
  Announcements,
  DndContextProps,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
} from '@dnd-kit/core';
import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  createContext,
  type HTMLAttributes,
  type ReactNode,
  useContext,
  useState,
  useRef,
  useEffect,
} from 'react';
import { createPortal } from 'react-dom';
import tunnel from 'tunnel-rat';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Card } from '@cotera/client/v0/components/ui/card';
import { cn } from '@cotera/client/v0/lib/utils';
import { useIntersectionObserver } from '../../data-grid/use-intersection-observer';

const t = tunnel();

export type { DragEndEvent } from '@dnd-kit/core';

export type KanbanItemProps = {
  id: string;
  name: string;
  column: string;
} & Record<string, unknown>;

type KanbanColumnProps = {
  id: string;
  name: string;
} & Record<string, unknown>;

type KanbanContextProps<
  T extends KanbanItemProps = KanbanItemProps,
  C extends KanbanColumnProps = KanbanColumnProps
> = {
  columns: C[];
  data: T[];
  activeCardId: string | null;
  disableDragging: boolean;
};

const KanbanContext = createContext<KanbanContextProps>({
  columns: [],
  data: [],
  activeCardId: null,
  disableDragging: false,
});

export type KanbanBoardProps = {
  id: string;
  children: ReactNode;
  className?: string;
};

export const KanbanBoard = ({ id, children, className }: KanbanBoardProps) => {
  const { isOver, setNodeRef } = useDroppable({
    id,
  });

  return (
    <div
      className={cn(
        'flex size-full min-h-40 flex-col overflow-hidden rounded-md bg-white text-xs ring-2 transition-all',
        isOver ? 'ring-primary' : 'ring-transparent',
        className
      )}
      ref={setNodeRef}
    >
      {children}
    </div>
  );
};

export type KanbanCardProps<T extends KanbanItemProps = KanbanItemProps> = T & {
  children?: ReactNode;
  className?: string;
};

export const KanbanCard = <T extends KanbanItemProps = KanbanItemProps>({
  id,
  name,
  children,
  className,
}: KanbanCardProps<T>) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transition,
    transform,
    isDragging,
  } = useSortable({
    id,
  });
  const { activeCardId, disableDragging } = useContext(
    KanbanContext
  ) as KanbanContextProps;

  const style = {
    transition,
    transform: CSS.Transform.toString(transform),
  };

  const dragProps = disableDragging ? {} : { ...listeners, ...attributes };

  return (
    <>
      <div style={style} {...dragProps} ref={setNodeRef}>
        <Card
          className={cn(
            'gap-4 rounded-md p-3',
            !disableDragging && 'cursor-grab',
            isDragging && 'pointer-events-none cursor-grabbing opacity-30',
            className
          )}
        >
          {children ?? <p className="m-0 font-medium text-sm">{name}</p>}
        </Card>
      </div>
      {activeCardId === id && (
        <t.In>
          <Card
            className={cn(
              'gap-4 rounded-md p-3 shadow-sm ring-2 ring-primary',
              !disableDragging && 'cursor-grab',
              isDragging && 'cursor-grabbing',
              className
            )}
          >
            {children ?? <p className="m-0 font-medium text-sm">{name}</p>}
          </Card>
        </t.In>
      )}
    </>
  );
};

export type KanbanCardsProps<T extends KanbanItemProps = KanbanItemProps> =
  Omit<HTMLAttributes<HTMLDivElement>, 'children' | 'id'> & {
    children: (item: T) => ReactNode;
    id: string;
    // New props for virtualization and infinite scrolling
    itemHeight?: number;
    overscan?: number;
    onLoadMore?: () => Promise<void> | void;
    hasMore?: boolean;
    loadingComponent?: ReactNode;
  };

export const KanbanCards = <T extends KanbanItemProps = KanbanItemProps>({
  children,
  className,
  itemHeight = 60, // Default card height
  overscan = 5,
  onLoadMore,
  hasMore = false,
  loadingComponent,
  ...props
}: KanbanCardsProps<T>) => {
  const { data, disableDragging } = useContext(
    KanbanContext
  ) as KanbanContextProps<T>;
  const filteredData = data.filter((item) => item.column === props.id);
  const items = filteredData.map((item) => item.id);

  // Virtualization setup
  const parentRef = useRef<HTMLDivElement>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const rowVirtualizer = useVirtualizer({
    count: filteredData.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => itemHeight,
    overscan,
    scrollPaddingEnd: 100, // Add some padding for better UX
  });

  // Intersection observer for infinite scrolling
  const [intersectionRef, loadMoreIntersectionVisible] =
    useIntersectionObserver({
      threshold: 0.1,
    });

  // Handle load more when intersection observer triggers
  useEffect(() => {
    if (
      loadMoreIntersectionVisible &&
      hasMore &&
      onLoadMore &&
      !isLoadingMore
    ) {
      const handleLoadMore = async () => {
        setIsLoadingMore(true);
        try {
          await onLoadMore();
        } finally {
          setIsLoadingMore(false);
        }
      };

      void handleLoadMore();
    }
  }, [loadMoreIntersectionVisible, hasMore, onLoadMore, isLoadingMore]);

  // Fallback: trigger load more when user scrolls near bottom
  useEffect(() => {
    if (!hasMore || isLoadingMore || !onLoadMore) {
      return;
    }

    const container = parentRef.current;
    if (!container) {
      return;
    }

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

      // Trigger when user is within 20% of the bottom
      if (scrollPercentage > 0.8) {
        const handleLoadMore = async () => {
          setIsLoadingMore(true);
          try {
            await onLoadMore();
          } finally {
            setIsLoadingMore(false);
          }
        };

        void handleLoadMore();
      }
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [hasMore, isLoadingMore, onLoadMore]);

  // Ensure virtualizer updates when data changes
  useEffect(() => {
    rowVirtualizer.measure();
  }, [filteredData.length, rowVirtualizer]);

  const virtualRows = rowVirtualizer.getVirtualItems();

  const content = (
    <div className={cn('flex flex-1 flex-col gap-2', className)} {...props}>
      {/* Virtualized content */}
      <div
        key={`virtual-container-${filteredData.length}`}
        style={{
          height: `${rowVirtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
          minHeight: `${filteredData.length * itemHeight}px`, // Ensure minimum height
        }}
      >
        {virtualRows.map((virtualRow) => {
          const item = filteredData[virtualRow.index];
          if (!item) {
            return null;
          }

          return (
            <div
              key={virtualRow.index}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: `${itemHeight}px`,
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              {children(item)}
            </div>
          );
        })}
      </div>

      {/* Intersection observer for infinite scrolling */}
      {hasMore && (
        <div
          ref={intersectionRef}
          className="h-4 w-full"
          style={{ minHeight: '16px' }}
        />
      )}

      {/* Loading indicator */}
      {isLoadingMore && (
        <div className="flex justify-center py-4 relative h-12">
          <div className="sticky inset-0 left-[50%] bottom-0 flex items-center gap-2">
            {loadingComponent ?? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                Loading more...
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div
      className="h-full overflow-auto"
      ref={parentRef}
      style={{
        scrollbarWidth: 'thin',
        scrollbarColor: 'rgba(0, 0, 0, 0.2) transparent',
      }}
    >
      {disableDragging ? (
        content
      ) : (
        <SortableContext items={items}>{content}</SortableContext>
      )}
    </div>
  );
};

export type KanbanHeaderProps = HTMLAttributes<HTMLDivElement>;

export const KanbanHeader = ({ className, ...props }: KanbanHeaderProps) => (
  <div className={cn('m-0 pb-2 font-semibold text-sm', className)} {...props} />
);

export type KanbanProviderProps<
  T extends KanbanItemProps = KanbanItemProps,
  C extends KanbanColumnProps = KanbanColumnProps
> = Omit<DndContextProps, 'children'> & {
  children: (column: C) => ReactNode;
  className?: string;
  columns: C[];
  data: T[];
  disableDragging?: boolean;
  onDataChange?: (data: T[]) => void;
  onDragStart?: (event: DragStartEvent) => void;
  onDragEnd?: (event: DragEndEvent) => void;
  onDragOver?: (event: DragOverEvent) => void;
};

export const KanbanProvider = <
  T extends KanbanItemProps = KanbanItemProps,
  C extends KanbanColumnProps = KanbanColumnProps
>({
  children,
  onDragStart,
  onDragEnd,
  onDragOver,
  className,
  columns,
  data,
  onDataChange,
  disableDragging = false,
  ...props
}: KanbanProviderProps<T, C>) => {
  const [activeCardId, setActiveCardId] = useState<string | null>(null);

  const touchSensor = useSensor(TouchSensor);

  const mouseSensor = useSensor(MouseSensor);

  const keyboardSensor = useSensor(KeyboardSensor);

  const sensors = useSensors(
    ...(disableDragging ? [] : [mouseSensor, touchSensor, keyboardSensor])
  );

  const handleDragStart = (event: DragStartEvent) => {
    const card = data.find((item) => item.id === event.active.id);
    if (card) {
      setActiveCardId(event.active.id as string);
    }
    onDragStart?.(event);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;

    if (!over) {
      return;
    }

    const activeItem = data.find((item) => item.id === active.id);
    const overItem = data.find((item) => item.id === over.id);

    if (!activeItem) {
      return;
    }

    const activeColumn = activeItem.column;
    const overColumn =
      overItem?.column ||
      columns.find((col) => col.id === over.id)?.id ||
      columns[0]?.id;

    if (activeColumn !== overColumn) {
      let newData = [...data];
      const activeIndex = newData.findIndex((item) => item.id === active.id);
      const overIndex = newData.findIndex((item) => item.id === over.id);

      newData[activeIndex]!.column = overColumn!;
      newData = arrayMove(newData, activeIndex, overIndex);

      onDataChange?.(newData);
    }

    onDragOver?.(event);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveCardId(null);

    onDragEnd?.(event);

    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    let newData = [...data];

    const oldIndex = newData.findIndex((item) => item.id === active.id);
    const newIndex = newData.findIndex((item) => item.id === over.id);

    newData = arrayMove(newData, oldIndex, newIndex);

    onDataChange?.(newData);
  };

  const announcements: Announcements = {
    onDragStart({ active }) {
      const { name, column } = data.find((item) => item.id === active.id) ?? {};

      return `Picked up the card "${name}" from the "${column}" column`;
    },
    onDragOver({ active, over }) {
      const { name } = data.find((item) => item.id === active.id) ?? {};
      const newColumn = columns.find((column) => column.id === over?.id)?.name;

      return `Dragged the card "${name}" over the "${newColumn}" column`;
    },
    onDragEnd({ active, over }) {
      const { name } = data.find((item) => item.id === active.id) ?? {};
      const newColumn = columns.find((column) => column.id === over?.id)?.name;

      return `Dropped the card "${name}" into the "${newColumn}" column`;
    },
    onDragCancel({ active }) {
      const { name } = data.find((item) => item.id === active.id) ?? {};

      return `Cancelled dragging the card "${name}"`;
    },
  };

  return (
    <KanbanContext.Provider
      value={{ columns, data, activeCardId, disableDragging }}
    >
      <DndContext
        accessibility={{ announcements }}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
        onDragStart={handleDragStart}
        sensors={sensors}
        {...props}
      >
        <div
          className={cn(
            'grid size-full auto-cols-fr grid-flow-col gap-4',
            className
          )}
        >
          {columns.map((column) => children(column))}
        </div>
        {typeof window !== 'undefined' &&
          createPortal(
            <DragOverlay>
              <t.Out />
            </DragOverlay>,
            document.body
          )}
      </DndContext>
    </KanbanContext.Provider>
  );
};
